// src/admin/AdminJazzCash.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../utils/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from './components/AdminPageWrapper';
// Note: Install xlsx library: npm install xlsx
// If not installed, uncomment the line below and install:
import * as XLSX from 'xlsx';

// JazzCash API endpoint
const JAZZCASH_EXPORT_ENDPOINT = API?.jazzCashExport || '/api/jazzcash/export';

export default function AdminJazzCash() {
  const [exchangeRate, setExchangeRate] = useState("");
  const [minimumBalancePKR, setMinimumBalancePKR] = useState(100.00);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exportData, setExportData] = useState([]);
  const [summary, setSummary] = useState(null);

  // Clear any stale error messages on mount
  useEffect(() => {
    setError("");
    setSuccess("");
    setExportData([]);
    setSummary(null);
  }, []);

  const handleExport = async () => {
    const exchangeRateNum = parseFloat(exchangeRate);
    const minimumBalanceNum = parseFloat(minimumBalancePKR) || 100.00;

    if (!exchangeRate || isNaN(exchangeRateNum) || exchangeRateNum <= 0) {
      setError("Please enter a valid exchange rate (USD to PKR)");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setExportData([]);
    setSummary(null);

    try {
      const requestBody = {
        exchangeRateUSDToPKR: exchangeRateNum,
        minimumBalancePKR: minimumBalanceNum,
      };
      
      console.log('📤 Sending JazzCash export request:', requestBody);
      
      const response = await api.post(JAZZCASH_EXPORT_ENDPOINT, requestBody);

      console.log('✅ JazzCash export response:', response.data);

      if (response.data.success) {
        setExportData(response.data.data || []);
        setSummary(response.data.summary || null);
        setSuccess(
          `Successfully exported ${response.data.data?.length || 0} users for JazzCash payout`
        );
      } else {
        setError(response.data.error || "Failed to export JazzCash payments");
      }
    } catch (err) {
      console.error("❌ JazzCash export error:", err);
      console.error("❌ Error response:", err.response?.data);
      console.error("❌ Error response status:", err.response?.status);
      console.error("❌ Full error object:", JSON.stringify(err.response?.data, null, 2));
      
      // Show detailed error information
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.details || 
                          err.message || 
                          "Failed to export JazzCash payments";
      
      // If we got a response with received data, show it
      if (err.response?.data?.received) {
        setError(`${errorMessage}\n\nReceived: ${JSON.stringify(err.response.data.received, null, 2)}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!exportData || exportData.length === 0) {
      setError("No data to export. Please generate the export first.");
      return;
    }

    try {
      // Prepare data for Excel (matching JazzCash format)
      const excelData = exportData.map((row) => ({
        "BENEFICIARY NAME": row["BENEFICIARY NAME"] || "",
        "MOBILE": row["MOBILE"] || "",
        "FT": row["FT"] || "FT",
        "TO BANK NAME": row["TO BANK NAME"] || "",
        "TO ACCOUNT NUMBER": row["TO ACCOUNT NUMBER"] || "",
        "AMOUNT": row["AMOUNT"] || 0,
        "Purpose Of Payment": row["Purpose Of Payment"] || "User Earnings From Dreamey Apps",
        "COMMENTS": row["COMMENTS"] || "",
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "JazzCash Payments");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const filename = `JazzCash_Payments_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);

      setSuccess(`Excel file "${filename}" downloaded successfully!`);
    } catch (err) {
      console.error("❌ Excel download error:", err);
      setError("Failed to generate Excel file: " + err.message);
    }
  };

  return (
    <AdminPageWrapper
      title="JazzCash Bulk Payout"
      subtitle="Export eligible users for JazzCash payments and generate Excel file"
    >
      <Grid container spacing={3}>
        {/* Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Export Configuration
              </Typography>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <TextField
                  label="Exchange Rate (USD to PKR)"
                  type="number"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ step: "0.01", min: "0.01" }}
                  helperText="Current USD to PKR exchange rate (e.g., 278.50)"
                  placeholder="278.50"
                />
                <TextField
                  label="Minimum Balance (PKR)"
                  type="number"
                  value={minimumBalancePKR}
                  onChange={(e) => setMinimumBalancePKR(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                  helperText="Minimum balance threshold in PKR (default: 100.00)"
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleExport}
                  disabled={loading || !exchangeRate}
                  startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  fullWidth
                >
                  {loading ? "Exporting..." : "Generate Export"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Card */}
        {summary && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Export Summary
                </Typography>
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Processed Users
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {summary.ProcessedUsers || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount (USD)
                    </Typography>
                    <Typography variant="h6">
                      ${(summary.TotalAmountUSD || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount (PKR)
                    </Typography>
                    <Typography variant="h6">
                      PKR {(summary.TotalAmountPKR || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Exchange Rate
                    </Typography>
                    <Typography variant="body1">
                      {summary.ExchangeRateUSDToPKR || exchangeRate} PKR/USD
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Minimum Balance
                    </Typography>
                    <Typography variant="body1">
                      PKR {summary.MinimumBalancePKR || minimumBalancePKR}
                    </Typography>
                  </Box>
                  {summary.JobExecutionId && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Job Execution ID
                      </Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {summary.JobExecutionId}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Messages */}
        {(error || success) && (
          <Grid item xs={12}>
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}
          </Grid>
        )}

        {/* Download Excel Button */}
        {exportData.length > 0 && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleDownloadExcel}
              startIcon={<DownloadIcon />}
              fullWidth
            >
              Download Excel File ({exportData.length} users)
            </Button>
          </Grid>
        )}

        {/* Data Preview Table */}
        {exportData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ mt: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>BENEFICIARY NAME</TableCell>
                      <TableCell>MOBILE</TableCell>
                      <TableCell>FT</TableCell>
                      <TableCell>AMOUNT (PKR)</TableCell>
                      <TableCell>Purpose Of Payment</TableCell>
                      <TableCell>COMMENTS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exportData.slice(0, 10).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row["BENEFICIARY NAME"]}</TableCell>
                        <TableCell>{row["MOBILE"]}</TableCell>
                        <TableCell>{row["FT"]}</TableCell>
                        <TableCell>
                          <Chip
                            label={`PKR ${(row["AMOUNT"] || 0).toFixed(2)}`}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{row["Purpose Of Payment"]}</TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {row["COMMENTS"]}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {exportData.length > 10 && (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing first 10 of {exportData.length} records. Download Excel to see all.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </AdminPageWrapper>
  );
}

